import { Form, Formik } from "formik";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { DatePicker } from "../../components/DatePicker";
import { Flex } from "../../components/Flex";
import { H2 } from "../../components/Heading";
import { Input } from "../../components/Input";
import { Paragraph } from "../../components/Paragraph";
import Select, { Option } from "../../components/Select";
import { newMonitoringFormValidation } from "../../utils/validationSchema";
import { MIN_AGE_APPLICATION } from "../../utils/constants";
import { useCommonTitles } from "../../data/useCommonTitles";
import { useState } from "react";
import { Modal } from "../../components/Modal";
import { NewMonitoringRecordFrame } from "./NewMonitoringRecordFrame";
import { MonitoringEventsFields } from "../../components/MonitoringEventsFields";
import { Box } from "../../components/Box";
import { useMembershipDivisionTeam } from "../../hooks/useMembershipDivisionTeam";
import styled from "styled-components";

const rowProps: any = {
  flexWrap: "wrap",
  gap: "16",
  my: "4",
};

const inputProps = {
  minWidth: 200,
  flex: 1,
};

const genderOptions = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
];

const MandateTypeBubble = ({ title, content, children }: any) => (
  <Box borderRadius={0} bg="#e3e7ee" p={3} mb={2}>
    <Paragraph mt="0px" fontWeight="bold">
      {title}
    </Paragraph>
    {content ? <Paragraph mb="0px">{content}</Paragraph> : children}
  </Box>
);

const StyledUl = styled.ul`
  ${({ theme: { fontSizes, fontFamily } }) => `     
      font-family: ${fontFamily};
      font-size: ${fontSizes[2]};   
      padding-left: 12px; 
      margin-bottom: 0px;
    `}
`;

export const StyledLi = styled.li`
  ${({ theme: { space, colors } }) => `
        margin-left: ${space[2]};

        ::marker{
        color: ${colors.gray[40]};
        }
  `}
`;

const Header = () => {
  return (
    <>
      <H2>Add Individual</H2>
      <Paragraph>
        Please add the individual you would like to create a monitoring record
        for.
      </Paragraph>
    </>
  );
};

export const NewIndividual = () => {
  const [doubleClick, setDoubleClick] = useState(false);
  const navigate = useNavigate();

  const [isNoDOBModalOpen, setIsNoDOBModalOpen] = useState(false);
  const [formData, setFormData] = useState(null);

  const {
    divisionOptions,
    showDivisionField,
    singleDivisionId,
    teamOptionsFor,
    singleTeamId,
  } = useMembershipDivisionTeam();

  const { data } = useCommonTitles();

  const titleOptions = data?.data.map(({ name }) => ({
    label: name,
    value: name,
  })) as Option[];

  const handleCreateIndividual = (form: any) => {
    setFormData(form);
    if (form.dateOfBirth) {
      handleSubmit(form);
    } else {
      setIsNoDOBModalOpen(true);
    }
  };

  const handleNext = () => {
    handleSubmit(formData);
  };

  const handleSubmit = (form) => {
    setDoubleClick(true);

    navigate(`/monitoring/new/confirm`, {
      state: { form },
    });
  };

  return (
    <>
      <NewMonitoringRecordFrame step="individual">
        <Formik
          initialValues={{
            yourReference: "",
            division: singleDivisionId,
            team: singleTeamId,
            title: "",
            firstName: "",
            middleName: "",
            lastName: "",
            dateOfBirth: "",
            gender: "",
            pep_and_sanction_required: "",
            adverse_media_required: "",
          }}
          validationSchema={newMonitoringFormValidation}
          onSubmit={handleCreateIndividual}
          enableReinitialize
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit} style={{ width: "510px" }}>
              <Header />
              <Box mb={3}>
                <Input
                  placeholder="Your Reference"
                  name="yourReference"
                  value={props.values.yourReference}
                  onChange={props.handleChange}
                  label="Your reference"
                />
              </Box>
              {showDivisionField && (
                <Flex {...rowProps}>
                  <Select
                    {...inputProps}
                    onChange={(option: any) => {
                      const newDivisionTeams = teamOptionsFor(option.value);
                      props.setValues({
                        ...props.values,
                        division: option.value,
                        team:
                          newDivisionTeams.length === 1
                            ? newDivisionTeams[0].value
                            : "",
                      });
                    }}
                    value={divisionOptions?.find(
                      ({ value }: any) => value === props.values.division
                    )}
                    options={divisionOptions}
                    placeholder="Choose division"
                    label="Division"
                    name="division"
                    isRequired
                    hasError={Boolean(props.errors.division)}
                    errorMessage={props.errors.division as string}
                  />
                </Flex>
              )}
              {teamOptionsFor(props.values.division).length > 0 && (
                <Flex {...rowProps}>
                  <Select
                    {...inputProps}
                    key={props.values.division}
                    onChange={(option: any) =>
                      props.setFieldValue("team", option.value)
                    }
                    value={
                      teamOptionsFor(props.values.division).find(
                        ({ value }: any) => value === props.values.team
                      ) ?? null
                    }
                    options={teamOptionsFor(props.values.division)}
                    placeholder="Choose team"
                    label="Team"
                    name="team"
                    isRequired
                    hasError={Boolean(props.errors.team)}
                    errorMessage={props.errors.team as string}
                  />
                </Flex>
              )}
              <Flex {...rowProps}>
                <div style={{ width: "48%" }}>
                  <Select
                    flex={1}
                    onChange={(option: any) =>
                      props.setFieldValue("title", option.value)
                    }
                    value={titleOptions?.find(
                      ({ value }) => value === props.values.title
                    )}
                    options={titleOptions}
                    placeholder="Select title"
                    mt="2"
                    label="Title"
                    name="title"
                    isRequired
                    hasError={Boolean(props.errors.title)}
                    errorMessage={props.errors.title as string}
                  />
                </div>
                <div style={{ width: "48%" }}>
                  <Select
                    flex={1}
                    onChange={(option: any) =>
                      props.setFieldValue("gender", option.value)
                    }
                    value={genderOptions?.find(
                      ({ value }) => value === props.values.gender
                    )}
                    options={genderOptions}
                    placeholder="Select gender"
                    mt="2"
                    label="Gender"
                    name="gender"
                    isRequired
                    hasError={Boolean(props.errors.gender)}
                    errorMessage={props.errors.gender as string}
                  />
                </div>
              </Flex>
              <Flex {...rowProps}>
                <Input
                  {...inputProps}
                  onChange={props.handleChange}
                  value={props.values.firstName}
                  placeholder="Enter first name"
                  mt="2"
                  label="First name"
                  name="firstName"
                  hasError={Boolean(props.errors.firstName)}
                  errorMessage={props.errors.firstName as string}
                  isRequired
                />
                <Input
                  {...inputProps}
                  onChange={props.handleChange}
                  value={props.values.middleName}
                  placeholder="Enter middle(s) name"
                  mt="2"
                  label="Middle name(s)"
                  name="middleName"
                  hasError={Boolean(props.errors.middleName)}
                  errorMessage={props.errors.middleName as string}
                />
              </Flex>
              <Flex {...rowProps}>
                <Input
                  {...inputProps}
                  onChange={props.handleChange}
                  value={props.values.lastName}
                  placeholder="Enter last name"
                  mt="2"
                  label="Last name"
                  name="lastName"
                  isRequired
                  hasError={Boolean(props.errors.lastName)}
                  errorMessage={props.errors.lastName as string}
                />
                <DatePicker
                  value={props.values.dateOfBirth as any}
                  onChange={(date: any) => {
                    props.setFieldValue("dateOfBirth", date);
                  }}
                  mt="2"
                  maxDate={DateTime.now()
                    .minus({ years: MIN_AGE_APPLICATION })
                    .toJSDate()}
                  label="Date of birth"
                  name="dateOfBirth"
                  hasError={Boolean(props.errors.dateOfBirth)}
                  errorMessage={props.errors.dateOfBirth as string}
                  {...inputProps}
                />
              </Flex>

              <MonitoringEventsFields
                formikProps={props}
                rowProps={rowProps}
                intro={
                  <Paragraph>
                    Please select the activities you would like to monitor.
                  </Paragraph>
                }
              />

              <Flex justifyContent="flex-end">
                {/* <Button
                size="large"
                mt="2"
                mb="0"
                variant="secondary"
                onClick={() =>
                  navigate(`/applications/${mandateId}/individuals`)
                }
              >
                Back
              </Button> */}
                <Button
                  size="large"
                  mt="2"
                  mb="2"
                  type="submit"
                  isDisabled={(props.dirty && !props.isValid) || doubleClick}
                >
                  Next
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>

        <Box
          display={{
            _: "none",
            xl: "block",
          }}
          position="absolute"
          right={4}
          top="205px"
          width="325px"
        >
          <Paragraph fontWeight="bold">Monitoring</Paragraph>
          <MandateTypeBubble
            title="Description"
            content="A monitoring record will be created for this individual. You will recieve a notification for any new PEP & sanctions, adverse media events. "
          />
          <MandateTypeBubble title="Events">
            <StyledUl>
              <StyledLi>PEP & sanctions screening</StyledLi>
              <Paragraph my={0} fontSize={1} color={"gray.60"}>
                A new potential PEP or sanctions result against the individual.
              </Paragraph>
              <StyledLi>Adverse media check</StyledLi>
              <Paragraph my={0} fontSize={1} color={"gray.60"}>
                A new potential adverse media match against the individual.
              </Paragraph>
            </StyledUl>
          </MandateTypeBubble>
        </Box>

        <Modal
          isOpen={isNoDOBModalOpen}
          onClose={() => setIsNoDOBModalOpen(false)}
          title="No date of birth"
          content="Although date of birth is not required, it improves the accuracy of the results. We recommend providing a date of birth before proceeding."
          onModalConfirmClick={handleNext}
          secondaryButtonText="Cancel"
          primaryButtonText="Continue anyway"
          primaryButtonVariant="danger"
          hasSecondaryButton
          width="450px"
        />
      </NewMonitoringRecordFrame>
    </>
  );
};
